import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Scale, MessageSquare, FileText, Folder, Plus, LogOut, 
  ChevronRight, Clock, Search, Settings, User, Shield, Workflow
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AuthContext } from "@/App";

// Use relative URL in production (deployed), or env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Admin roles that can access admin console
const ADMIN_ROLES = ["admin", "partner", "psl"];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [workspaces, setWorkspaces] = useState([]);
  const [recentChats, setRecentChats] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const isAdmin = ADMIN_ROLES.includes(user?.role?.toLowerCase() || "");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [workspacesRes, chatsRes, docsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/workspaces`),
        axios.get(`${BACKEND_URL}/api/chats`),
        axios.get(`${BACKEND_URL}/api/documents`)
      ]);
      
      setWorkspaces(workspacesRes.data);
      setRecentChats(chatsRes.data.slice(0, 5));
      setDocuments(docsRes.data.slice(0, 5));
      
      if (workspacesRes.data.length > 0) {
        setSelectedWorkspace(workspacesRes.data[0]);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`);
      toast.success("Logged out successfully");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const startNewChat = (type = "research") => {
    navigate("/chat", { state: { chatType: type, workspaceId: selectedWorkspace?.workspace_id } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="dashboard">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Scale className="w-7 h-7 text-[#C5A059]" />
            <span className="font-playfair text-xl font-semibold text-[#E2E8F0]">TALC</span>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input 
                placeholder="Search matters, documents..."
                data-testid="dashboard-search"
                className="pl-10 w-64 bg-[#15171C] border-white/10 text-[#E2E8F0] placeholder:text-[#64748B] rounded-sm h-9"
              />
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-[#C5A059] text-[#0B0C10] font-medium">
                      {user?.name?.[0] || "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#15171C] border-white/10">
                <DropdownMenuItem className="text-[#E2E8F0]">
                  <User className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem className="text-[#E2E8F0]">
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem 
                      onClick={() => navigate("/admin")} 
                      className="text-[#C5A059]"
                      data-testid="admin-console-link"
                    >
                      <Shield className="w-4 h-4 mr-2" /> Admin Console
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={handleLogout} className="text-[#EF4444]" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 border-r border-white/10 min-h-[calc(100vh-65px)] p-4">
          <div className="mb-6">
            <p className="text-[#64748B] font-mono text-xs uppercase tracking-widest mb-3">Workspaces</p>
            {workspaces.map((ws) => (
              <button
                key={ws.workspace_id}
                onClick={() => setSelectedWorkspace(ws)}
                data-testid={`workspace-${ws.workspace_id}`}
                className={`w-full text-left px-3 py-2 rounded-sm mb-1 transition-colors ${
                  selectedWorkspace?.workspace_id === ws.workspace_id 
                    ? 'bg-[#C5A059]/10 text-[#C5A059] border border-[#C5A059]/30' 
                    : 'text-[#94A3B8] hover:bg-white/5'
                }`}
              >
                <Folder className="w-4 h-4 inline mr-2" />
                <span className="font-manrope text-sm">{ws.name}</span>
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <Button 
              onClick={() => startNewChat("research")}
              data-testid="new-research-btn"
              className="w-full justify-start bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm text-xs uppercase tracking-wide"
            >
              <Plus className="w-4 h-4 mr-2" /> New Research
            </Button>
            <Button 
              onClick={() => navigate("/documents")}
              data-testid="view-documents-btn"
              variant="outline" 
              className="w-full justify-start border-white/20 text-[#E2E8F0] hover:bg-white/5 rounded-sm text-xs uppercase tracking-wide"
            >
              <FileText className="w-4 h-4 mr-2" /> Documents
            </Button>
            <Button 
              onClick={() => navigate("/matters")}
              data-testid="view-matters-btn"
              variant="outline" 
              className="w-full justify-start border-white/20 text-[#E2E8F0] hover:bg-white/5 rounded-sm text-xs uppercase tracking-wide"
            >
              <Folder className="w-4 h-4 mr-2" /> Matters
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="mb-8">
            <h1 className="font-playfair text-3xl font-semibold text-[#E2E8F0] mb-2">
              Welcome back, {user?.name?.split(' ')[0] || 'Counsel'}
            </h1>
            <p className="text-[#94A3B8] font-manrope">
              {selectedWorkspace ? `Working in ${selectedWorkspace.name}` : 'Select a workspace to begin'}
            </p>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <QuickAction 
              icon={<Search className="w-5 h-5" />}
              title="Legal Research"
              description="Search UK case law and legislation"
              onClick={() => startNewChat("research")}
              testId="quick-action-research"
            />
            <QuickAction 
              icon={<FileText className="w-5 h-5" />}
              title="Contract Review"
              description="Analyze and review contracts"
              onClick={() => navigate("/documents")}
              testId="quick-action-review"
            />
            <QuickAction 
              icon={<MessageSquare className="w-5 h-5" />}
              title="Draft Document"
              description="Generate legal documents"
              onClick={() => startNewChat("drafting")}
              testId="quick-action-draft"
            />
            <QuickAction 
              icon={<Workflow className="w-5 h-5" />}
              title="Workflows"
              description="Run automated legal workflows"
              onClick={() => navigate("/workflows")}
              testId="quick-action-workflows"
            />
          </div>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Chats */}
            <Card className="bg-[#15171C] border-white/5 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-playfair text-lg text-[#E2E8F0]">Recent Conversations</h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-[#C5A059] hover:bg-[#C5A059]/10"
                  onClick={() => navigate("/chat")}
                >
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              {recentChats.length === 0 ? (
                <p className="text-[#64748B] text-sm font-manrope">No conversations yet. Start a new research query.</p>
              ) : (
                <div className="space-y-2">
                  {recentChats.map((chat) => (
                    <button
                      key={chat.chat_id}
                      onClick={() => navigate(`/chat/${chat.chat_id}`)}
                      data-testid={`chat-${chat.chat_id}`}
                      className="w-full text-left p-3 rounded-sm bg-[#0B0C10] hover:bg-[#1E2028] transition-colors border border-white/5"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-[#E2E8F0] font-manrope text-sm truncate">{chat.title}</p>
                          <p className="text-[#64748B] font-mono text-xs mt-1 flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {new Date(chat.updated_at).toLocaleDateString()}
                          </p>
                        </div>
                        <span className="text-[#C5A059] font-mono text-xs uppercase">{chat.chat_type}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Card>

            {/* Recent Documents */}
            <Card className="bg-[#15171C] border-white/5 rounded-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-playfair text-lg text-[#E2E8F0]">Recent Documents</h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-[#C5A059] hover:bg-[#C5A059]/10"
                  onClick={() => navigate("/documents")}
                >
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              {documents.length === 0 ? (
                <p className="text-[#64748B] text-sm font-manrope">No documents uploaded. Upload your first document.</p>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div
                      key={doc.document_id}
                      data-testid={`doc-${doc.document_id}`}
                      className="p-3 rounded-sm bg-[#0B0C10] border border-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[#C5A059]" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[#E2E8F0] font-manrope text-sm truncate">{doc.filename}</p>
                          <p className="text-[#64748B] font-mono text-xs">
                            {(doc.file_size / 1024).toFixed(1)} KB • {doc.doc_type}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

const QuickAction = ({ icon, title, description, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="bg-[#15171C] border border-white/5 rounded-sm p-6 text-left hover:border-[#C5A059]/30 transition-all duration-300 hover:-translate-y-1 group"
  >
    <div className="text-[#C5A059] mb-3 group-hover:scale-110 transition-transform">{icon}</div>
    <h3 className="font-playfair text-lg text-[#E2E8F0] mb-1">{title}</h3>
    <p className="text-[#94A3B8] font-manrope text-sm">{description}</p>
  </button>
);

export default Dashboard;
